<template>
  <v-container class="pa-0 fill-height">
    <v-dialog
      v-model="displayWhatsNew"
      v-if="whatsNew"
      width="500"
      persistent
    >
      <v-card class="d-flex flex-column text-center py-2">
        <v-card-title class="text-h5 mb-3 d-block">
          {{ $t("home.whatsNew.title") }}
        </v-card-title>
        <v-card-subtitle>
          {{ $t("home.whatsNew.subtitle") }}
        </v-card-subtitle>
        <v-list>
          <v-list-item
            v-for="(item, index) in whatsNew.features"
            :key="`feature-${index}`"
            class="align-start mb-5"
          >
            <v-list-item-icon
              class="flex-shrink-0"
              style="margin-top: 2px"
            >
              <v-img
                :src="item.iconUrl"
                height="40"
                width="40"
              />
            </v-list-item-icon>
            <div class="text-left">
              {{ item.description }}
            </div>
          </v-list-item>
        </v-list>
        <v-card-actions>
          <v-btn
            color="primary"
            large
            block
            elevation="0"
            @click="toggleWhatsNew(whatsNew.targetVersion)"
          >
            {{ $t("home.whatsNew.buttonLabel") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-sheet
      ref="header"
      style="width:100%;background-color:#CBEDFD;display:flex;"
    >
      <div class="pb-2">
        <v-container class="pa-0">
          <v-row
            no-gutters
            class="ma-0"
          >
            <v-col>
              <div class="d-flex align-start flex-column">
                <h1 class="text-h5 font-weight-bold primary--text px-6">
                  {{ welcomeText }}
                </h1>
              </div>
            </v-col>
          </v-row>
        </v-container>
      </div>
    </v-sheet>
    <v-row
      class="list-content"
      :style="`display: ${contentDisplay}; top: ${headerHeight}px;`"
    >
      <v-sheet
        style="width:100%;background-color:#CBEDFD;display:flex;"
        v-if="shouldShowCarousel"
      >
        <div class="background">
          <GenericCarousel
            :items="this.sortedNews"
            :is-loading="isLoadingNews"
            :is-loading-error="isLoadingNewsError"
            :no-items-message="noItemsMessage"
            :load-items="loadNews"
            :on-click="openNewsItem"
            generic-image="/img/generic-news.svg"
          />
        </div>
      </v-sheet>
      <v-container>
        <v-row class="ma-0">
          <v-col>
            <v-list class="py-0">
              <v-list-item
                class="px-0 mb-3"
                v-for="item in listItems"
                :key="item.key"
              >
                <v-card
                  @click="navigate(item.key)"
                  class="px-4 py-3 d-flex flex-grow-1 align-center"
                >
                  <div class="mr-6">
                    <v-img
                      :src="item.icon"
                      height="60"
                      width="60"
                    />
                  </div>
                  <div
                    style="font-size: 18px;"
                    class="font-weight-medium"
                  >
                    {{ item.label }}
                  </div>
                  <div
                    class="new-badge v-chip"
                    v-if="isNewItem(item.key)"
                  >
                    {{ $t("home.newBadge") }}
                  </div>
                </v-card>
              </v-list-item>

              <v-divider
                style="max-width: none"
                class="my-5 mx-n5"
              />
              <v-list-item class="px-0">
                <v-card
                  @click="surveyClick()"
                  class="px-4 py-3 d-flex flex-grow-1 align-center"
                >
                  <div class="mr-6">
                    <v-img
                      src="/img/feedback.svg"
                      height="60"
                      width="60"
                    />
                  </div>
                  <div>
                    <div
                      style="font-size: 18px;"
                      class="font-weight-medium primary--text mb-1"
                    >
                      {{ $t("home.feedback.title") }}
                    </div>
                    <div
                      style="font-size: 15px"
                      class="font-weight-medium"
                    >
                      {{ $t("home.feedback.desc") }}
                    </div>
                  </div>
                </v-card>
              </v-list-item>
            </v-list>
          </v-col>
        </v-row>
      </v-container>
    </v-row>
  </v-container>
</template>

<script>
import { mapGetters } from "vuex";
import features from "@/features.json";
import GenericCarousel from "../components/GenericCarousel.vue";
import CarouselCard from "../components/CarouselCard.vue";
import enrolment from "@/views/Enrolment.vue";
import { FullscreenDialog } from "@nswdoe/doe-ui-core";
import packageInfo from "../../package.json";
import semver from "semver";

const Home = {
  name: "Home",
  components: {
    GenericCarousel,
    CarouselCard,
    FullscreenDialog
  },
  title: "Home",
  data: () => ({
    headerHeight: 200,
    resizeObserver: new ResizeObserver(function(entries) {
      if (entries[0]) {
        const rect = entries[0].target.getBoundingClientRect();
        this.headerHeight = rect.height;
        this.contentDisplay = "block";
      }
    }),
    isLoadingNews: false,
    isLoadingNewsError: false,
    itemCount: 5,
    contentDisplay: "block",
    whatsNew: null,
    homeScreenNewItems: []
  }),
  created() {
    window.addEventListener(
      "remoteConfigChanged",
      function() {
        //eslint-disable-next-line
        NativeJS.debugLog(`PWA remote config changed`);
        this.whatsNew = this.remoteConfig("whatsNew");
        this.homeScreenNewItems = this.remoteConfig("homeScreenNewItems");
      }.bind(this)
    );
  },
  mounted() {
    this.resizeObserver.observe(this.$refs.header.$el);
    const rect = this.$refs.header.$el.getBoundingClientRect();
    this.headerHeight = rect.height;
    this.contentDisplay = "block";
    this.loadNews();

    this.whatsNew = this.remoteConfig("whatsNew");
    this.homeScreenNewItems = this.remoteConfig("homeScreenNewItems");
  },
  beforeDestroy() {
    this.resizeObserver.unobserve(this.$refs.header.$el);
  },
  computed: {
    enrolment() {
      return enrolment;
    },
    ...mapGetters([
      "mySchools",
      "user",
      "news",
      "whatsNewHistory",
      "appFilters"
    ]),
    firstSchool: function() {
      return this.mySchools[0].schoolId;
    },
    welcomeText: function() {
      if (this.user && this.user != "error") {
        return `${this.$t("home.welcome")}, ${this.user.given_name}`;
      } else {
        return this.$t("home.welcome");
      }
    },
    noItemsMessage: function() {
      return this.isLoadingNewsError
        ? this.$t("home.carousel.error")
        : this.$t("home.carousel.empty");
    },
    sortedNews: function() {
      const sortedNews = [...this.news.items].sort(function(a, b) {
        // move pinned events to the top
        if (a.pinned) {
          return -1;
        } else {
          return -1 * (new Date(a.created) - new Date(b.created)); // we don't want to give preference to global news here, so we sort again.
        }
      });
      return sortedNews ? sortedNews.slice(0, this.itemCount) : [];
    },
    displayWhatsNew: function() {
      let targetVersion = this.whatsNew.targetVersion;
      let appVersion = packageInfo.version.split("-")[0];

      if (this.whatsNewHistory.includes(targetVersion)) {
        return false;
      } else {
        if (
          semver.eq(appVersion, targetVersion) ||
          semver.patch(appVersion, targetVersion)
        ) {
          return true;
        } else {
          return false;
        }
      }
    },
    listItems: function() {
      return [
        {
          icon: "/img/news-feed.svg",
          label: this.$t("home.news"),
          key: "news"
        },
        {
          icon: "/img/my-schools.svg",
          label: this.$t("home.mySchools"),
          key: "mySchools"
        },
        {
          icon: "/img/events.svg",
          label: this.$t("home.events"),
          key: "events"
        },
        {
          icon: "/img/newsletters.svg",
          label: this.$t("home.newsletters"),
          key: "newsletters"
        },
        {
          icon: "/img/enrolment.svg",
          label: this.$t("home.enrolment"),
          key: "enrolment"
        },
        {
          icon: "/img/learning-resources.svg",
          label: this.$t("home.learningResources"),
          key: "learningResources"
        }
      ];
    },
    shouldShowCarousel: function() {
      const { newsFilter } = this.appFilters;
      return newsFilter.some(filter => filter.value);
    }
  },
  methods: {
    externalLink(link) {
      //eslint-disable-next-line
      NativeJS.debugLog(`opening external link (${link})`);
      //eslint-disable-next-line
      NativeJS.externalLink(link);
    },
    openInApp(link, title) {
      //eslint-disable-next-line
      NativeJS.debugLog(`open ${title}`);
      this.setCookie("deeplinkType", "HOME", 3650);
      this.setCookie("deeplinkUrl", "HOME", 3650);
      if (parseInt(this.getCookie("appbuild")) >= features.inAppWebPage) {
        //eslint-disable-next-line
        NativeJS.debugLog("open url");
        //eslint-disable-next-line
        NativeJS.openUrl(
          link,
          JSON.stringify({
            title: title
          })
        );
      } else {
        //eslint-disable-next-line
        NativeJS.externalLink(link);
      }
    },
    async loadNews() {
      try {
        this.isLoadingNews = true;
        await this.$store.dispatch("getNews", {
          page: 0,
          itemsPerPage: 10,
          hideSpinner: true
        });
        this.isLoadingNews = false;
      } catch (error) {
        this.isLoadingNews = false;
        this.isLoadingNewsError = true;
      }
    },
    openNewsItem(newsItem) {
      this.$router.push(`/news/${encodeURIComponent(newsItem.id)}`);
      this.$router.push({
        path: `/news/${encodeURIComponent(newsItem.id)}`,
        query: {
          returnRoute: `${encodeURIComponent("/")}`
        }
      });
    },
    navigate(route) {
      let analyticsParams = null;
      switch (route) {
        case "mySchools":
          route = `school/${this.firstSchool}`;
          break;
        case "enrolment":
          analyticsParams = {
            action: "button_click",
            item_category: "enrolment_type",
            label: "online_school_enrolment"
          };
          break;
        case "learningResources":
          route = "learning-resources";
          analyticsParams = {
            action: "feature_click",
            item_category: "learning_resources"
          };
          break;
      }
      if (analyticsParams) {
        this.analytics("button_click", analyticsParams);
      }
      this.$router.push(route);
    },
    isNewItem(item) {
      return this.homeScreenNewItems.indexOf(item) > -1;
    },
    toggleWhatsNew: function(targetVersion) {
      this.$store.dispatch("setWhatsNewHistory", targetVersion);
    },
    surveyClick: function() {
      this.analytics("feedback", {
        action: "button_click",
        item_category: "survey",
        label: "give_us_your_feedback"
      });
      this.openInApp(this.remoteConfig("surveyUrl"), "Feedback");
    }
  }
};

export default Home;
</script>

<style lang="scss" scoped>
h2 {
  font-size: 1.125rem;
}
.background {
  background: url("/img/home-backdrop.svg") center center;
  background-size: cover;
  width: 100%;
}
.v-subheader {
  font-size: 1.125rem;
  font-weight: 500;
}
.icon-button {
  border-radius: 15px !important;
  background-color: #cbedfd;
  padding: 13px 9px 14px 16px;
  &.enrolment {
    padding: 8px;
  }
}
.row {
  margin: 0;
}
.list-content {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  overflow-y: scroll;
}
.new-badge {
  position: absolute;
  top: 5px;
  right: 5px;
  border-radius: 12px;
  font-size: 0.8rem;
  color: #fff;
  background: #407ec9;
}
</style>
